import { ChangeEvent } from "react"

import TextField, { TextFieldProps } from "@material-ui/core/TextField"
import { css, cx } from "@emotion/css"
import { formatFloatingPointNumber, parseNumber } from "utils/number-format"

import { ReactComponent as LockedIcon } from "assets/icon-lock.svg"
import { ReactComponent as UnLockedIcon } from "assets/icon-unlock.svg"
import { useField } from "formik"
import { useI18n } from "providers/I18n"
import { useRifm } from "rifm"

type Extra = {
  alignRight?: boolean
  errorMessage?: string
}
export type TextInputProps = TextFieldProps & Extra

export function TextInput(props: TextInputProps) {
  const {
    value,
    error,
    errorMessage,
    helperText,
    InputProps,
    alignRight,
    className,
    size = "small",
    margin = "none",
    ...rest
  } = props
  const { t } = useI18n()
  const _error = !!errorMessage || error
  const helper = _error ? t(errorMessage!) : helperText
  return (
    <TextField
      size={size}
      margin={margin}
      variant="outlined"
      value={value}
      error={_error}
      helperText={helper}
      className={className}
      InputProps={{
        classes: {
          root: cx({
            [styles.rightAligned]: alignRight,
          }),
        },
        ...InputProps,
      }}
      {...rest}
    />
  )
}

export function InputWithLock(
  props: Omit<TextInputProps, "onChange"> & {
    onChange: (args: { name: string; value?: string }) => void
    locked?: boolean
    onLockClick: () => void
    isMonetary?: boolean
  },
) {
  const {
    locked,
    onLockClick,
    disabled,
    value = "",
    name = "",
    isMonetary,
    onChange,
    ...inputProps
  } = props

  const rifm = useRifm({
    value: !!value ? `${value}` : "",
    accept: isMonetary ? /[\d.]/g : undefined,
    onChange: (v) =>
      onChange(
        isMonetary
          ? { name, value: parseNumber(v) }
          : { name, value: value as string },
      ),
    format: isMonetary
      ? (v) => {
          const parsed = parseNumber(v)
          const number = parseInt(parsed)
          if (isNaN(number)) {
            return ""
          }
          return formatFloatingPointNumber(2, 2)(v)
        }
      : (v) => v,
  })

  const handleChange = isMonetary
    ? rifm.onChange
    : (evt: ChangeEvent<HTMLInputElement>) => {
        onChange({ name, value: evt.target.value })
      }

  return (
    <div className={styles.lockInputContainer}>
      <TextInput
        fullWidth
        {...inputProps}
        onChange={handleChange}
        value={rifm.value}
        disabled={disabled || locked}
      />
      <button
        data-testid={`${name.toLowerCase()}-lock-button`}
        title={locked ? "Unlock" : "Lock"}
        className={styles.locker}
        onClick={onLockClick}
      >
        {locked && <LockedIcon className={styles.lockerIcon} />}
        {!locked && <UnLockedIcon className={styles.lockerIcon} />}
      </button>
    </div>
  )
}

type PropsHandledByFormik = "onChange" | "value" | "error"
type NameRequired = { name: string }
export type TextInputFormikProps = Omit<TextInputProps, PropsHandledByFormik> &
  NameRequired

export function TextInputFormik(props: TextInputFormikProps) {
  const { name, ...rest } = props
  const [{ value, ...field }, { error, touched }] = useField<string>(name)
  const hasError = !!(touched && error)
  const errorMessage = hasError ? error : undefined

  const v = value ?? ""
  return (
    <TextInput {...rest} value={v} errorMessage={errorMessage} {...field} />
  )
}

type NumberInputFormikProps = TextInputFormikProps & {
  maxDigits?: number
  minDigits?: number
}

export function NumberInputFormik(props: NumberInputFormikProps) {
  const { name, maxDigits = 2, minDigits = 2, ...rest } = props
  const [{ value }, { error, touched }, { setValue }] = useField<
    string | number
  >(name)
  const hasError = !!(touched && error)
  const errorMessage = hasError ? error : undefined

  const rifm = useRifm({
    value: !!value ? `${value}` : "",
    accept: /[\d.]/g,
    onChange: (v) => setValue(parseNumber(v)),
    format: (v) => {
      const parsed = parseNumber(v)
      const number = parseInt(parsed)
      if (isNaN(number)) {
        return ""
      }
      return formatFloatingPointNumber(maxDigits, minDigits)(v)
    },
  })

  return (
    <TextInput
      {...rest}
      value={rifm.value}
      onChange={rifm.onChange}
      errorMessage={errorMessage}
    />
  )
}

const styles = {
  rightAligned: css`
    input {
      text-align: right;
    }
  `,
  lockInputContainer: css`
    position: relative;
  `,
  locker: css`
    position: absolute;
    right: calc(-1 * var(--spacing-s));
    bottom: var(--spacing-xs);
    background-color: var(--light-grey-2);
    border: 0;
    border-radius: 30px;
    width: var(--spacing-xl);
    height: var(--spacing-xl);
    cursor: pointer;
    padding: 0;
  `,
  lockerIcon: css`
    margin-top: -1px;
  `,
}
